import inherits from 'inherits-browser';
import BaseRenderer from 'diagram-js/lib/draw/BaseRenderer';
import { is } from 'bpmn-js/lib/util/ModelUtil';
import { getFillColor, getStrokeColor } from 'bpmn-js/lib/draw/BpmnRenderUtil';
import {
  append as svgAppend,
  attr as svgAttr,
  create as svgCreate,
} from 'tiny-svg';

/**
 * Renders textAnnotation elements that carry custom:itSystem="true" as a
 * visual IT-System / computer-icon shape.
 *
 * Signavio represents external IT systems with a proprietary computer icon
 * that has no BPMN equivalent. After import-cleanup, these annotations are
 * tagged with custom:itSystem="true" so the BPMN stays valid while this
 * renderer provides the visual context business analysts expect.
 *
 * Rendering:
 *   ┌──────────────────┐
 *   │   ┌──────────┐   │   <- grey background rect
 *   │   │  screen  │   │   <- monitor (white + blue inner)
 *   │   └────┬─┬───┘   │
 *   │        │ │        │   <- stand neck
 *   │      ──────       │   <- base
 *   │   System name     │   <- text centered below icon
 *   └──────────────────┘
 */

const HIGH_PRIORITY = 1500;

const COLORS = {
  bg: '#e8e8e8',
  border: '#888888',
  screenOuter: '#555555',
  screenInner: '#cce0f5',
  stand: '#888888',
  text: '#222222',
};

function isItSystem(element) {
  if (!element || !element.businessObject) return false;
  if (!is(element, 'bpmn:TextAnnotation')) return false;
  return element.businessObject.get('custom:itSystem') === true;
}

/**
 * Draw a simple computer-monitor icon centered at (cx, topY) within iconSize.
 */
function drawComputerIcon(parent, cx, topY, iconSize) {
  const screenW = iconSize * 0.82;
  const screenH = iconSize * 0.58;
  const screenX = cx - screenW / 2;
  const screenY = topY;
  const innerPad = Math.max(2, iconSize * 0.06);

  // Outer screen casing
  const screenEl = svgCreate('rect');
  svgAttr(screenEl, {
    x: screenX, y: screenY,
    width: screenW, height: screenH,
    rx: 2, ry: 2,
    fill: '#ffffff',
    stroke: COLORS.screenOuter,
    'stroke-width': 1.5,
  });
  svgAppend(parent, screenEl);

  // Inner screen (blue tint)
  const innerEl = svgCreate('rect');
  svgAttr(innerEl, {
    x: screenX + innerPad, y: screenY + innerPad,
    width: screenW - innerPad * 2, height: screenH - innerPad * 2,
    fill: COLORS.screenInner,
    stroke: 'none',
  });
  svgAppend(parent, innerEl);

  // Stand neck
  const neckW = Math.max(4, iconSize * 0.14);
  const neckH = iconSize * 0.18;
  const neckX = cx - neckW / 2;
  const neckY = screenY + screenH;
  const neckEl = svgCreate('rect');
  svgAttr(neckEl, {
    x: neckX, y: neckY,
    width: neckW, height: neckH,
    fill: COLORS.stand,
    stroke: 'none',
  });
  svgAppend(parent, neckEl);

  // Base
  const baseW = iconSize * 0.52;
  const baseH = Math.max(3, iconSize * 0.07);
  const baseX = cx - baseW / 2;
  const baseY = neckY + neckH;
  const baseEl = svgCreate('rect');
  svgAttr(baseEl, {
    x: baseX, y: baseY,
    width: baseW, height: baseH,
    rx: 1, ry: 1,
    fill: COLORS.stand,
    stroke: 'none',
  });
  svgAppend(parent, baseEl);

  // Total icon height from topY
  return screenH + neckH + baseH;
}

export default function ItSystemRenderer(eventBus, textRenderer) {
  BaseRenderer.call(this, eventBus, HIGH_PRIORITY);
  this._textRenderer = textRenderer;
}

inherits(ItSystemRenderer, BaseRenderer);

ItSystemRenderer.$inject = ['eventBus', 'textRenderer'];

ItSystemRenderer.prototype.canRender = function (element) {
  return isItSystem(element);
};

ItSystemRenderer.prototype.drawShape = function (parent, element) {
  const w = element.width;
  const h = element.height;

  // Background — honour Signavio/DI colours when present, else fall back
  // to the neutral grey IT-System styling.
  const bg = svgCreate('rect');
  svgAttr(bg, {
    x: 0, y: 0,
    width: w, height: h,
    rx: 4, ry: 4,
    fill: getFillColor(element, COLORS.bg),
    stroke: getStrokeColor(element, COLORS.border),
    'stroke-width': 1.5,
  });
  svgAppend(parent, bg);

  // Icon: scale to available space, cap at 48px, leave margin
  const iconSize = Math.min(w * 0.55, h * 0.52, 48);
  const marginTop = Math.max(6, h * 0.08);
  const iconTopY = marginTop;
  const cx = w / 2;

  const iconHeight = drawComputerIcon(parent, cx, iconTopY, iconSize);

  // Text below icon
  const textTopY = iconTopY + iconHeight + 4;
  const textAvailH = Math.max(12, h - textTopY - 4);
  const textAvailW = w - 8;
  const text = (element.businessObject.text || '').trim();

  if (text && textAvailH > 4) {
    const textEl = this._textRenderer.createText(text, {
      box: {
        width: textAvailW,
        height: textAvailH,
        x: textAvailW / 2,
        y: 0,
      },
      align: 'center-top',
      padding: 2,
      style: {
        fontFamily: 'Arial, sans-serif',
        fontSize: '11px',
        fontWeight: 'normal',
        fill: COLORS.text,
      },
    });
    svgAttr(textEl, { transform: `translate(4, ${textTopY})` });
    svgAppend(parent, textEl);
  }

  return bg;
};

ItSystemRenderer.prototype.getShapePath = function (shape) {
  const { x, y, width, height } = shape;
  return [
    ['M', x, y],
    ['l', width, 0],
    ['l', 0, height],
    ['l', -width, 0],
    ['z'],
  ];
};
